// Created on 2023-08-03
// Created by Vitaut Tryputsin
//
// Copyright (c) 2023 OPEN CASCADE SAS
//
// This file is part of commercial software by OPEN CASCADE SAS,
// furnished in accordance with the terms and conditions of the contract
// and with the inclusion of this copyright notice.
// This file or any part thereof may not be provided or otherwise
// made available to any third party.
//
// No ownership title to the software is transferred hereby.
//
// OPEN CASCADE SAS makes no representation or warranties with respect to the
// performance of this software, and specifically disclaims any responsibility
// for any damages, special or consequential, connected with its use.
import { Camera } from "./camera.js";
/**
 * Subview definition - a region in canvas for displaying 3D viewer content.
 */
export class Subview extends EventTarget {
    /** Empty constructor. */
    constructor(theViewId = -2) {
        super();
        this._myViewId = theViewId;
        this._myViewMgr = null;
        this._myCamera = null;
    }
    /**
      * Create new subview.
      * @param {OccViewer} theViewer viewer module
      * @param {WasmVector2d} theSize the subview size
      */
    create(theViewer, theSize) {
        this._myViewMgr = theViewer.viewManager;
        this._myViewId = this._myViewMgr.createSubview(theSize);
        this._myViewMgr.addSubviewObject(this);
        this._myCamera = new Camera(theViewer, this._myViewId);
    }
    /**
      * Remove subview.
      */
    remove() {
        if (this._myViewMgr != null && this._myViewId != -2) {
            this._myViewMgr.removeSubview(this._myViewId);
            this._myViewId = -2;
            this._myCamera = null;
        }
    }
    /**
      * Wrap existing view.
      * @param {OccViewer} theViewer viewer module
      * @param {ViewId} theId the id to wrap
      * @return {boolean} FALSE if view with specified Id doesn't exist in the Viewer
      */
    wrap(theViewer, theId = -2) {
        if (theId !== -2) {
            this._myViewId = theId;
        }
        this._myViewMgr = theViewer.viewManager;
        this._myCamera = new Camera(theViewer, this._myViewId);
        return this._myViewMgr.isDefined(this._myViewId);
    }
    /** Get view id as [[ViewId]]; -2 is invalid id, -1 refers to a view receiving input focus. */
    get id() { return this._myViewId; }
    /** Return TRUE if view with specified Id exists. */
    get isDefined() {
        return this._myViewMgr != null && this._myViewMgr.isDefined(this._myViewId);
    }
    /** Return TRUE if view is active (displayed on the screen). */
    get isActive() {
        return this._myViewMgr != null && this._myViewMgr.isActive(this._myViewId);
    }
    /** Set view active state. */
    set isActive(theActive) { this._myViewMgr.setActive(this._myViewId, theActive); }
    /** Return camera controller. */
    get camera() { return this._myCamera; }
    /** Return TRUE if view receives input focus. */
    get hasInputFocus() {
        return this._myViewMgr != null && this._myViewMgr.hasInputFocus(this._myViewId);
    }
    /** Set this view to receive input focus. */
    setInputFocus() { this._myViewMgr.setInputFocus(this._myViewId); }
    /** Return TRUE if view serves as a composer to subviews. */
    get isSubviewComposer() {
        return this._myViewMgr != null && this._myViewMgr.isSubviewComposer(this._myViewId);
    }
    /** Set view composer state. */
    set isSubviewComposer(theComposer) {
        this._myViewMgr.setSubviewComposer(this._myViewId, theComposer);
    }
    /** Return subview size as a fraction to canvas dimensions. */
    get subviewSize() { return this._myViewMgr.getSubviewSize(this._myViewId); }
    /** Set subview size. */
    set subviewSize(theSize) {
        this._myViewMgr.setSubviewSize(this._myViewId, theSize);
    }
    /** Return subview offset from the corner as a fraction to canvas dimensions. */
    get subviewOffset() {
        return this._myViewMgr.getSubviewOffset(this._myViewId);
    }
    /** Set subview offset. */
    set subviewOffset(theOffset) {
        this._myViewMgr.setSubviewOffset(this._myViewId, theOffset);
    }
    /** Return subview margins in pixels. */
    get subviewMargins() {
        return this._myViewMgr.getSubviewMargins(this._myViewId);
    }
    /** Set subview margins in pixels. */
    set subviewMargins(theMargins) {
        this._myViewMgr.setSubviewMargins(this._myViewId, theMargins);
    }
    /** Return subview corner. */
    get subviewCorner() {
        return this._myViewMgr.getSubviewCorner(this._myViewId);
    }
    /** Set subview corner. */
    set subviewCorner(theCorner) {
        this._myViewMgr.setSubviewCorner(this._myViewId, theCorner);
    }
    /** Return TRUE if subview should fill entire canvas (replaces all subviews). */
    get isSubviewFullscreen() {
        return this._myViewMgr.isSubviewFullscreen(this._myViewId);
    }
    /** Set if subview should fill entire canvas (replace all subviews) or not. */
    set isSubviewFullscreen(theToEnable) {
        this._myViewMgr.setSubviewFullscreen(this._myViewId, theToEnable);
    }
    /** Get subview background color. */
    get subviewBackgroundColor() {
        return this._myViewMgr.getSubviewBackgroundColor(this._myViewId);
    }
    /** Set subview background color. */
    set subviewBackgroundColor(theBgColor) {
        this._myViewMgr.setSubviewBackgroundColor(this._myViewId, theBgColor);
    }
}
